/* @flow */
import { Container } from 'flux/utils';
import React, { Component } from 'react';

import AffiliationStore from 'stores/AffiliationStore';
import AppLayoutContentView from 'app/components/AppLayoutContentView.jsx';
import AppLayoutContentViewHeader from 'app/components/AppLayoutContentViewHeader.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import CurrentUser from 'auth/CurrentUser';
import { EventTrackingConstants } from 'constants/EventTrackingConstants';
import GroupActions from 'groups/actions/GroupActions';
import GroupKeyboardListener from 'groups/components/GroupKeyboardListener.jsx';
import GroupStore from 'groups/stores/GroupStore';
import JoinMergeAccountsModal from 'join/components/JoinMergeAccountsModal.jsx';
import MessageActions from 'groups/actions/MessageActions';
import OrganizationStore from 'stores/OrganizationStore';
import RequireDesktop from 'components/mixins/RequireDesktop';
import RequireSetup from 'setup/utils/RequireSetup';
import RequireUser from 'components/mixins/RequireUser';
import UniversalComposeButtonContainer from 'chat/containers/UniversalComposeButtonContainer.jsx';
import UniversalComposer, { PANELS } from 'chat/components/UniversalComposer.jsx';
import { mountModal } from 'lib/modal';
import chainOnEnter from 'components/lib/chainOnEnter';
import {
  getOrderedSectionInfo,
  defaultItemOf,
} from 'groups/lib/groupNavigation';

import type { RouterState, LocationDescriptor } from 'lib/ReactRouterTypes';

const checkInformationRequired = {
  statics: {
    onEnter(nextState: RouterState, replaceWith: (location: LocationDescriptor) => void, callback: Function) {
      if (GroupStore.isLoaded()) {
        callback();
      } else {
        GroupActions.loadGroups().then(callback, (err) => {
          if (err.code === 'information_required') {
            replaceWith('/information_required');
          }
          callback();
        });
      }
    },
  },
};

class GroupsView extends Component {
  props: {
    children?: mixed,
    location: Object,
  };

  state: {
    showDraft: boolean,
    navSections: Array<Object>,
  };

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  static childContextTypes = {
    eventContextName: React.PropTypes.string,
  };

  static getStores() {
    return [
      GroupStore,
      AffiliationStore,
      OrganizationStore,
    ];
  }

  static onEnter = chainOnEnter([
    RequireUser,
    RequireSetup,
    RequireDesktop,
    checkInformationRequired,
  ])

  static calculateState() {
    // XXX transitioning to a shared interface for org groups and class groups
    const ownedGroups = GroupStore.getOwnedGroups();
    const subscribedGroups = GroupStore.getSubscribedGroups();
    const districtGroups = GroupStore.getDistrictGroups();
    const schoolGroups = GroupStore.getSchoolGroups();
    const organizations = AffiliationStore.getAffiliatedOrganizations();
    const userCanOwnGroups = CurrentUser.getUser().canOwnGroups();
    const hasAdminConsole = CurrentUser.getUser().hasFeature('web_admin_console');

    const navSections = getOrderedSectionInfo({
      organizations,
      districtGroups,
      schoolGroups,
      ownedGroups,
      subscribedGroups,
      userCanOwnGroups,
      options: {
        ignoreDistrictSection: !hasAdminConsole,
        ignoreDistrictOrganizationSection: hasAdminConsole,
      },
    });

    return {
      showDraft: GroupStore.getShowDraft(),
      navSections,
    };
  }

  getChildContext() {
    return {
      eventContextName: EventTrackingConstants.UNIVERSAL_COMPOSER_CLASSES,
    };
  }

  componentDidMount() {
    const query = this.props.location.query;

    // XXX Does this work?
    if ('token' in query) {
      mountModal(
        <JoinMergeAccountsModal
          token={query.token}
          onRequestHide={
            code => code && this.context.router.replace(`/classes/${code}`)
          }
        />, this);
    }
  }

  componentDidUpdate() {
    if (this.state.showDraft) {
      const canAnnounce = CurrentUser.getUser().canCreateGroups();
      mountModal(
        <UniversalComposer
          canAnnounce={canAnnounce}
          panel={PANELS.CLASS_ANNOUCEMENT}
        />, this);
      // hack, clean up when we have a non-imperative modal
      MessageActions.finishShowingDraft();
    }
  }

  displayUnsavedChangesDialog() {
    return new Promise(resolve => {
      mountModal(
        <ConfirmDialog
          eventScreenName="GroupsView"
          actionButtonText={'Discard'}
          cancelButtonText={'Cancel'}
          size="small"
          title={'Are you sure you want to discard this announcement?'}
          onConfirm={resolve}
        />, this);
    });
  }

  renderComposeButton() {
    if (!CurrentUser.getUser().canChat()) {
      return null;
    }
    return (
      <UniversalComposeButtonContainer
        full
        eventScreenName="groups_view"
        eventElementName="new_message_button"
      />
    );
  }

  renderHeader() {
    const title = 'Classes';

    return (
      <AppLayoutContentViewHeader title={title}>
        {this.renderComposeButton()}
      </AppLayoutContentViewHeader>
    );
  }

  render() {
    const { navSections } = this.state;

    // XXX until we merge org and group interfaces
    const defaultGroupOrOrg = defaultItemOf(navSections);
    const { children } = this.props;

    return (
      <AppLayoutContentView header={this.renderHeader()}>
        {
          // $FlowIssue Flow incorrectly thinks children is undefined
          React.cloneElement(children, { defaultGroupOrOrg })
        }
        <GroupKeyboardListener />
      </AppLayoutContentView>
    );
  }
}

export default Container.create(GroupsView, { pure: false });



// WEBPACK FOOTER //
// ./src/groups/components/GroupsView.jsx